import { useIntl } from "react-intl";
import { isValidElement, useId } from "react";
import { CheckCircleIcon, ChevronDownIcon } from "./icons";

// A native <select> styled via the CSS customizable-select API (see `.nguard-select`
// in index.css). In Chromium 135+ a custom trigger (the <button>/<selectedcontent>
// slot) mirrors the other fields: value on the left, the field label echoed on
// the right with a valid check, matching ui/Input's formField look. Other
// browsers fall back to the plain native select (value + native arrow).
//
// Props: options = [{ value, label }]; onChange receives the native change event.

function renderError(error) {
  if (!error) return null;
  if (typeof error === "string" || isValidElement(error)) return error;
  return error.defaultMessage ?? error.message ?? null;
}

export function Select({
  id,
  name,
  value,
  onChange,
  options = [],
  label,
  hint,
  error,
  required,
  autoComplete,
  className,
  wrapperClassName,
}) {
  const intl = useIntl();
  const uid = useId();
  const selectId = id ?? uid;
  const hintId = `${uid}-hint`;
  const errorId = `${uid}-error`;
  const hasError = Boolean(error);

  return (
    <div className={`flex w-full flex-col gap-sm ${wrapperClassName ?? ""}`}>
      {label && (
        <label htmlFor={selectId} className="sr-only">
          {label}
          {required
            ? ` ${intl.formatMessage({ id: "auth.requiredSuffix", defaultMessage: "(required)" })}`
            : ""}
        </label>
      )}
      <select
        id={selectId}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? errorId : hint ? hintId : undefined}
        className={`nguard-select ${hasError ? "nguard-select--error" : ""} ${className ?? ""}`}
      >
        {/* Custom trigger (Chromium base-select only; ignored in fallback). */}
        <button type="button" className="nguard-select__trigger">
          <selectedcontent className="nguard-select__value"></selectedcontent>
          {label && <span className="nguard-select__echo">{label}</span>}
          <CheckCircleIcon className="nguard-select__check" size={18} />
          <ChevronDownIcon className="nguard-select__chevron" size={18} />
        </button>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {hint && !hasError && (
        <p
          id={hintId}
          className="text-xs font-body leading-xs text-text-default-light dark:text-text-default-dark"
        >
          {hint}
        </p>
      )}
      {hasError && (
        <p
          id={errorId}
          role="alert"
          className="text-xs font-body leading-xs text-text-error-light dark:text-text-error-dark"
        >
          {renderError(error)}
        </p>
      )}
    </div>
  );
}

export default Select;
